import { clientImportTemplateCsv, clientImportTemplateRows, readClientSheet, type ImportClientRow } from '@selfon/domain/browser';
import { useState } from 'react';
import { api, type ImportResultRow, type StaffRow } from '../api';
import { Alert, Dialog, errorText } from '../components/ui';
import { readTable, SheetError } from '../sheet';
import { makeXlsx } from '../xlsx-write';
import { saveBytes } from '../zip';

/**
 * 엑셀/CSV로 이용자 여러 명 등록: 파일 읽기 → 서버 미리보기(dryRun) → 오류 없으면 등록.
 * 보호자 계정은 만들지 않는다. 보호자는 등록 후 아동별 연결 코드로 연결한다.
 */
export function ClientImport({ centerId, staff, open, onClose, onDone }: {
  centerId: string; staff: StaffRow[]; open: boolean; onClose: () => void; onDone: (n: number) => void;
}) {
  const [rows, setRows] = useState<ImportClientRow[]>([]);
  const [sheetErrors, setSheetErrors] = useState<Array<{ line: number; message: string }>>([]);
  const [preview, setPreview] = useState<ImportResultRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reset = () => { setRows([]); setSheetErrors([]); setPreview(null); setError(null); };

  const pick = async (file: File | undefined) => {
    reset();
    if (!file) return;
    try {
      const r = readClientSheet(await readTable(file), staff);
      setRows(r.rows); setSheetErrors(r.errors);
      if (r.rows.length && !r.errors.length) setPreview((await api.importClients(centerId, r.rows, true)).results);
    } catch (e) { setError(e instanceof SheetError ? e.message : errorText(e)); }
  };
  const template = () => {
    const url = URL.createObjectURL(new Blob([clientImportTemplateCsv()], { type: 'text/csv;charset=utf-8' }));
    const a = Object.assign(document.createElement('a'), { href: url, download: '이용자-일괄등록-양식.csv' });
    a.click(); URL.revokeObjectURL(url);
  };
  const templateXlsx = () => saveBytes('이용자-일괄등록-양식.xlsx', makeXlsx(clientImportTemplateRows(), '이용자'), 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
  const failed = (preview ?? []).filter((r) => !r.ok).length;
  const ready = rows.length > 0 && !sheetErrors.length && preview !== null && failed === 0;

  return (
    <Dialog wide title="엑셀로 이용자 등록" open={open} onClose={() => { reset(); onClose(); }} submitLabel={ready ? `${rows.length}명 등록` : '등록'}
      onSubmit={async () => {
        if (!ready) throw new Error('파일을 고르고 미리보기에 오류가 없어야 등록할 수 있습니다.');
        const r = await api.importClients(centerId, rows, false);
        reset(); onDone(r.created);
      }}>
      <p className="hint">첫 줄은 머리글입니다: 이름, 출생연도, 회기단가, 담당 선생님(이름, 여러 명은 쉼표), 목표(여러 개는 ;), 목표 기간(선택, 2026-03-01~2026-08-31), 장애유형, 보호자 전화, 주소, 메모. 한 번에 200명까지 올릴 수 있습니다.</p>
      <p className="hint">보호자 계정은 만들지 않습니다. 등록한 뒤 아동마다 연결 코드를 만들어 보호자에게 전해 주세요.</p>
      <div className="row">
        <button type="button" className="btn btn-outline btn-sm" onClick={templateXlsx}>양식 받기(엑셀 .xlsx)</button>
        <button type="button" className="btn btn-outline btn-sm" onClick={template}>양식 받기(CSV)</button>
        <input type="file" aria-label="엑셀 파일" accept=".xlsx,.csv,.xls,.html" onChange={(e) => void pick(e.target.files?.[0])} />
      </div>
      {error && <Alert>{error}</Alert>}
      {sheetErrors.length > 0 && (
        <Alert>파일에서 고칠 곳이 있습니다(아무것도 등록하지 않았습니다).
          <ul>{sheetErrors.slice(0, 30).map((x, i) => <li key={i}>{x.line}번째 줄: {x.message}</li>)}</ul>
        </Alert>
      )}
      {preview && (
        <>
          <Alert kind={failed ? 'error' : 'info'}>{failed ? `${failed}줄에 오류가 있어 등록할 수 없습니다. 파일을 고쳐 다시 올려 주세요.` : `${preview.length}명을 등록할 수 있습니다. 확인 후 등록을 누르세요.`}</Alert>
          <table className="table">
            <thead><tr><th>줄</th><th>이름</th><th>결과</th></tr></thead>
            <tbody>{preview.map((r) => (
              <tr key={r.line}><td>{r.line}</td><td>{r.displayName ?? rows.find((x) => x.line === r.line)?.displayName}</td>
                <td>{r.ok ? (r.warning ? `확인 필요: ${r.warning}` : '등록 가능') : `오류: ${r.error}`}</td></tr>
            ))}</tbody>
          </table>
        </>
      )}
    </Dialog>
  );
}
